import { DEFAULT_LOCALE } from '../utils/i18n'
import type { RouteDefinition, RouteParams } from './types'

/**
 * Categories that should NOT have locale prefixes
 */
const NO_LOCALE_PREFIX_CATEGORIES = new Set([
  'webflow',
  'external-app',
  'external-url',
  'legal',
])

/**
 * Build a URL from a route definition and optional parameters
 */
export function buildUrl<TPath extends string>(
  route: RouteDefinition<TPath>,
  params: RouteParams<TPath>,
  options?: { locale?: string }
): string {
  let path = route.path as string

  // Replace path parameters
  if (params) {
    for (const [key, value] of Object.entries(
      params as Record<string, string>
    )) {
      path = path.replace(`:${key}`, encodeURIComponent(value))
    }
  }

  // Apply locale prefix if needed
  if (!NO_LOCALE_PREFIX_CATEGORIES.has(route.category)) {
    const locale = options?.locale
    if (locale && locale !== DEFAULT_LOCALE) {
      const shortLocale = locale.split('-')[0]
      path = `/${shortLocale}${path}`
    }
  }

  return path
}

/**
 * Check if value is a route definition
 */
function isRouteDefinition(value: unknown): value is RouteDefinition {
  return (
    typeof value === 'object' &&
    value !== null &&
    'path' in value &&
    'category' in value
  )
}

/**
 * Type for a route builder function
 */
type RouteBuilder<TPath extends string> =
  RouteParams<TPath> extends void
    ? (options?: { locale?: string }) => string
    : (params: RouteParams<TPath>, options?: { locale?: string }) => string

/**
 * Create a route builder with a default locale baked in
 */
function createRouteBuilder<TPath extends string>(
  route: RouteDefinition<TPath>,
  defaultLocale: string
): RouteBuilder<TPath> {
  const hasParams = route.path.includes(':')

  if (hasParams) {
    return ((
      params: RouteParams<TPath>,
      options?: { locale?: string }
    ): string => {
      return buildUrl(route, params, {
        locale: options?.locale ?? defaultLocale,
      })
    }) as RouteBuilder<TPath>
  }

  return ((options?: { locale?: string }): string => {
    return buildUrl(route, undefined as RouteParams<TPath>, {
      locale: options?.locale ?? defaultLocale,
    })
  }) as RouteBuilder<TPath>
}

/**
 * Recursively create URL builders from route definitions
 */
type UrlBuilders<T> = {
  [K in keyof T]: T[K] extends RouteDefinition<infer TPath>
    ? RouteBuilder<TPath>
    : T[K] extends object
      ? UrlBuilders<T[K]>
      : T[K]
}

export function createUrlBuilders<T extends object>(
  routeObj: T,
  locale: string
): UrlBuilders<T> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(routeObj)) {
    if (isRouteDefinition(value)) {
      result[key] = createRouteBuilder(value, locale)
    } else if (typeof value === 'object' && value !== null) {
      result[key] = createUrlBuilders(value as object, locale)
    } else {
      result[key] = value
    }
  }

  return result as UrlBuilders<T>
}
